// ─── APP LAYOUT ───────────────────────────────────────────────────────────────
// Main application shell with:
//   - Collapsible sidebar navigation
//   - Top header with user menu
//   - Responsive layout (mobile drawer)
//   - Keyboard navigation support
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2, LayoutDashboard, Users, Contact, Home, Calendar, FileText,
  DollarSign, Scale, MessageCircle, BarChart3, Settings, Handshake,
  ChevronLeft, ChevronRight, LogOut, Search, Menu, X, TrendingUp, UserCog
} from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { useLogout } from '~/modules/auth/hooks/useAuth';
import { PERFIS_LABELS } from '@aksape/constants';
import { NotificacaoBell } from '~/modules/agenda/components/NotificacaoBell';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', perfis: ['admin', 'gestor', 'corretor', 'juridico', 'financeiro', 'assistente'] },
  { to: '/pessoas', icon: Contact, label: 'Pessoas', perfis: ['admin', 'gestor', 'corretor', 'juridico', 'financeiro', 'assistente'] },
  { to: '/leads', icon: Users, label: 'Leads / CRM', perfis: ['admin', 'gestor', 'corretor', 'assistente'] },
  { to: '/imoveis', icon: Home, label: 'Imóveis', perfis: ['admin', 'gestor', 'corretor', 'juridico', 'financeiro'] },
  { to: '/agenda', icon: Calendar, label: 'Agenda', perfis: ['admin', 'gestor', 'corretor', 'assistente', 'juridico', 'financeiro'] },
  { to: '/locacoes', icon: FileText, label: 'Locações', perfis: ['admin', 'gestor', 'juridico', 'financeiro'] },
  { to: '/negociacoes', icon: Handshake, label: 'Negociações', perfis: ['admin', 'gestor', 'corretor', 'juridico'] },
  { to: '/vendas', icon: Building2, label: 'Vendas', perfis: ['admin', 'gestor', 'corretor', 'juridico'] },
  { to: '/financeiro', icon: DollarSign, label: 'Financeiro', perfis: ['admin', 'gestor', 'financeiro'] },
  { to: '/juridico', icon: Scale, label: 'Jurídico', perfis: ['admin', 'juridico'] },
  { to: '/relacionamento', icon: MessageCircle, label: 'Relacionamento', perfis: ['admin', 'gestor', 'corretor', 'assistente', 'juridico', 'financeiro'] },
  { to: '/relatorios', icon: BarChart3, label: 'Relatórios', perfis: ['admin', 'gestor', 'corretor', 'juridico', 'financeiro'] },
  // Sprint 3.6, Fase A — Inteligência Comercial / BI.
  { to: '/comercial-bi', icon: TrendingUp, label: 'Inteligência Comercial', perfis: ['admin', 'gestor', 'corretor', 'financeiro'] },
];

export function AppLayout() {
  const { user, tenant } = useAuthStore();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout(undefined, { onSuccess: () => navigate('/auth/login') });
  };

  const visibleItems = NAV_ITEMS.filter(
    (item) => !user?.perfil || item.perfis.includes(user.perfil),
  );

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <aside
      className={`
        flex flex-col h-full bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))]
        transition-all duration-300 ease-in-out
        ${mobile ? 'w-64' : collapsed ? 'w-[72px]' : 'w-[220px]'}
      `}
    >
      {/* Brand */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-[hsl(var(--sidebar-border))]
        ${collapsed && !mobile ? 'justify-center' : ''}`}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
          {tenant?.configuracoes?.logoUrl ? (
            <img src={tenant.configuracoes.logoUrl} alt={tenant.nomeImobiliaria} className="w-full h-full object-contain" />
          ) : (
            <img src="/logo-icon.png" alt="AKSAPÊ" className="w-full h-full object-contain" />
          )}
        </div>
        {(!collapsed || mobile) && (
          <div className="min-w-0">
            <p className="font-bold text-sm text-[hsl(var(--sidebar-foreground))] truncate">
              {tenant?.nomeImobiliaria ?? 'AKSAPÊ'}
            </p>
            <p className="text-2xs text-[hsl(var(--sidebar-muted-foreground))]">Enterprise</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => mobile && setMobileOpen(false)}
            className={({ isActive }) =>
              `nav-item ${isActive ? 'active' : ''} ${collapsed && !mobile ? 'justify-center px-0' : ''}`
            }
            title={collapsed && !mobile ? item.label : undefined}
          >
            <item.icon className="w-4 h-4 shrink-0" />
            {(!collapsed || mobile) && (
              <span className="text-sm truncate">{item.label}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Settings & Collapse */}
      <div className="border-t border-[hsl(var(--sidebar-border))] p-2 space-y-0.5">
        <NavLink
          to="/minha-conta"
          className={({ isActive }) =>
            `nav-item ${isActive ? 'active' : ''} ${collapsed && !mobile ? 'justify-center px-0' : ''}`
          }
          title={collapsed && !mobile ? 'Minha Conta' : undefined}
        >
          <UserCog className="w-4 h-4 shrink-0" />
          {(!collapsed || mobile) && <span className="text-sm">Minha Conta</span>}
        </NavLink>

        <NavLink
          to="/configuracoes"
          className={({ isActive }) =>
            `nav-item ${isActive ? 'active' : ''} ${collapsed && !mobile ? 'justify-center px-0' : ''}`
          }
        >
          <Settings className="w-4 h-4 shrink-0" />
          {(!collapsed || mobile) && <span className="text-sm">Configurações</span>}
        </NavLink>

        {/* User info */}
        <div
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mt-1
            ${collapsed && !mobile ? 'justify-center' : ''}`}
        >
          <div className="w-7 h-7 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">
              {user?.nomeCompleto?.[0]?.toUpperCase() ?? 'U'}
            </span>
          </div>
          {(!collapsed || mobile) && (
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-[hsl(var(--sidebar-foreground))] truncate">
                {user?.nomeCompleto?.split(' ')[0]}
              </p>
              <p className="text-2xs text-[hsl(var(--sidebar-muted-foreground))] truncate">
                {PERFIS_LABELS[user?.perfil ?? ''] ?? user?.perfil}
              </p>
            </div>
          )}
          {(!collapsed || mobile) && (
            <button
              onClick={handleLogout}
              className="text-[hsl(var(--sidebar-muted-foreground))] hover:text-destructive transition-colors"
              title="Sair"
              aria-label="Sair da conta"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Collapse toggle — desktop only */}
        {!mobile && (
          <button
            onClick={() => setCollapsed((v) => !v)}
            className={`w-full nav-item ${collapsed ? 'justify-center px-0' : ''}`}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Recolher</span>
              </>
            )}
          </button>
        )}
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex h-full">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full flex">
            <SidebarContent mobile />
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-white"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="h-14 border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0">
          {/* Mobile menu toggle */}
          <button
            className="lg:hidden text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Search bar */}
          <div className="hidden sm:flex flex-1 max-w-md ml-4 lg:ml-0">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="search"
                placeholder="Buscar leads, imóveis, clientes..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Header actions */}
          <div className="flex items-center gap-2 ml-auto">
            <NotificacaoBell />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
